import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { InfoIcon } from 'components/icons/Info'
import { Spinner } from 'components/Spinner'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

export const ProfileTransactions = observer(function ProfileTransactions() {
  const { tt } = useTranslationTyped()
  const breakoutUser = useBreakoutUser()

  const {
    availableTokens,
    expiredTokens,
    consumedTokens: consumedTokenCount,
    sectionPassRebateAvailableCents,
    sectionPassRebateConsumedCents,
  } = breakoutUser

  const mappedSlideDecksToConsumedTokens =
    breakoutUser.mappedSlideDecksToConsumedTokens

  const consumedTokens = useMemo(
    () =>
      breakoutUser.tokens
        // todo(ashold12): modify transaction UI to support section passes
        .filter(
          (token) =>
            token.data.consumed && token.data.purchaseType !== 'sectionPass'
        )
        .sort((a, b) => {
          if (!a.data.purchaseDate || !b.data.purchaseDate) {
            return 0
          }
          return (
            new Date(b.data.purchaseDate).getTime() -
            new Date(a.data.purchaseDate).getTime()
          )
        }),
    [breakoutUser.tokens]
  )

  const loadingMappedSlideDecks =
    typeof mappedSlideDecksToConsumedTokens === 'undefined' &&
    consumedTokens.length > 0

  const { availableRebate, consumedRebate, showRebateInfo } = useMemo(() => {
    if (!sectionPassRebateAvailableCents && !sectionPassRebateConsumedCents)
      return { availableRebate: '', consumedRebate: '', showRebateInfo: false }

    const availableRebate = tt.student_library.dollars({
      dollars: (sectionPassRebateAvailableCents / 100).toFixed(2),
    })

    const consumedRebate = tt.student_library.dollars({
      dollars: (sectionPassRebateConsumedCents / 100).toFixed(2),
    })

    return {
      availableRebate,
      consumedRebate,
      showRebateInfo: true,
    }
  }, [
    sectionPassRebateAvailableCents,
    sectionPassRebateConsumedCents,
    tt.student_library,
  ])

  return (
    <div>
      <h2 className="text-headline-large text-core-on-tertiary">
        {tt.profile.transactions()}
      </h2>
      <p className="mt-1 text-body-large">
        {tt.profile.transactions_description()}
      </p>
      <div className="mt-1 flex flex-wrap justify-evenly gap-x-4 text-nowrap text-label-medium text-core-on-tertiary 2xl:grid 2xl:grid-cols-3">
        <div
          className={classNames('flex items-center py-2', {
            'justify-center': !showRebateInfo,
            'justify-start': showRebateInfo,
          })}
        >
          {tt.profile.tokens_available()}{' '}
          <strong className="ml-1 text-title-medium">
            {availableTokens ?? 0}
          </strong>
        </div>
        <div
          className={classNames('flex items-center py-2', {
            'justify-center': !showRebateInfo,
            'justify-start': showRebateInfo,
          })}
        >
          {tt.profile.tokens_consumed()}{' '}
          <strong className="ml-1 text-title-medium text-core-error">
            {consumedTokenCount ?? 0}
          </strong>
        </div>
        <div
          className={classNames('flex items-center py-2', {
            'justify-center': !showRebateInfo,
            'justify-start': showRebateInfo,
          })}
        >
          <BreakoutTooltip content={tt.profile.tokens_expired_description()}>
            <div aria-label={tt.profile.tokens_expired_description()}>
              <InfoIcon
                size={15}
                className="mr-1 flex-shrink-0 cursor-pointer"
              />
            </div>
          </BreakoutTooltip>
          <span>{tt.profile.tokens_expired()} </span>
          <strong
            className={classNames('ml-1 text-title-medium', {
              'text-core-error': expiredTokens > 0,
            })}
          >
            {expiredTokens ?? 0}
          </strong>
        </div>
        {showRebateInfo && (
          <>
            <div className="flex items-center justify-start py-2">
              {tt.profile.section_pass_credit_available()}
              <strong className="ml-1 text-title-medium">
                {availableRebate}
              </strong>
            </div>
            <div className="flex items-center justify-start py-2">
              {tt.profile.section_pass_credit_consumed()}
              <strong className="ml-1 text-title-medium text-core-error">
                {consumedRebate}
              </strong>
            </div>
          </>
        )}
      </div>

      <ul className="mt-6 list-none space-y-2">
        {loadingMappedSlideDecks && <Spinner size={3} />}
        {!loadingMappedSlideDecks &&
          consumedTokens.map((token) => {
            const slideDeck = mappedSlideDecksToConsumedTokens?.[token.id]
            if (!slideDeck) {
              return null
            }

            // Ex. 28 Jul 2024
            const formattedDate = token.data.purchaseDate
              ? DateTime.fromJSDate(new Date(token.data.purchaseDate)).toFormat(
                  'dd LLL yyyy'
                )
              : ''

            return (
              <li key={token.id}>
                <div
                  role="group"
                  aria-label={tt.profile.transaction()}
                  className="flex h-[75px] w-full items-center justify-between rounded-2xl bg-surface px-5"
                >
                  <div className="flex items-center">
                    <div className="shrink-0 p-2.5">
                      <img
                        alt=""
                        src={slideDeck.data.slideDeckImageURL}
                        className="h-5 w-5"
                      />
                    </div>
                    <div className="overflow-hidden">
                      <h3 className="max-w-[150px] overflow-hidden truncate text-body-medium text-on-surface-var lg:max-w-[300px]">
                        {slideDeck.data.slideDeckName}
                      </h3>
                      <div className="max-w-[150px] overflow-hidden truncate text-label-large text-core-on-tertiary lg:max-w-[300px]">
                        {slideDeck.data.slideDeckTeaser}
                      </div>
                      <div className="max-w-[150px] overflow-hidden truncate text-body-medium text-on-surface-var lg:max-w-[300px]">
                        {formattedDate}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-title-large text-core-on-tertiary">
                      {token.data.consumedCount}
                    </div>
                    <div className="text-body-small text-on-surface-var">
                      {tt.profile.tokens()}
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
      </ul>
    </div>
  )
})
